// react-refresh: non-component export in component file
/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService, AuthUser } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { User } from '@/types/roles';
import { getErrorMessage } from '@/lib/errors';
import { logger } from '../lib/logger';

interface AuthContextType {
  user: User | null;
  supabaseUser: AuthUser | null;
  loading: boolean;
  signInWithOtp: (email: string) => Promise<void>;
  verifyOtpAndSetPassword: (email: string, token: string, password?: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function toAuthUser(user: { id: string; email?: string | null }): AuthUser {
  return {
    id: user.id,
    email: user.email ?? '',
  };
}

async function fetchUserProfile(userId: string): Promise<User | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, salon_id, organization_id, role, is_active, must_change_password, full_name, email, avatar_url, phone')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data as User | null) ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    let active = true;

    const syncUserState = async (authUser: AuthUser | null) => {
      if (!active) {
        return;
      }

      setSupabaseUser(authUser);

      if (!authUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const profile = await fetchUserProfile(authUser.id);
        if (active) {
          setUser(profile);
        }
      } catch (error) {
        if (active) {
          setUser(null);
        }
        logger.error('[useAuth] Error al obtener perfil:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    authService
      .getSession()
      .then((session) => syncUserState(session?.user ? toAuthUser(session.user) : null))
      .catch((error) => {
        logger.error('[useAuth] Error al recuperar la sesión:', error);
        if (active) {
          setUser(null);
          setSupabaseUser(null);
          setLoading(false);
        }
      });

    const {
      data: { subscription },
    } = authService.onAuthStateChange((authUser) => {
      void syncUserState(authUser);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    queryClient.clear();
  }, [queryClient, supabaseUser?.id]);

  const signInWithOtp = async (email: string) => {
    try {
      await authService.sendOtp(email);
      toast.success('Codigo de verificacion enviado a tu email');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Error al enviar el codigo'));
      throw error;
    }
  };

  const verifyOtpAndSetPassword = async (email: string, token: string, password?: string) => {
    try {
      const { user: authUser } = await authService.verifyOtpAndSetPassword(email, token, password);

      if (!authUser) {
        throw new Error('No se pudo verificar la cuenta.');
      }

      setSupabaseUser(toAuthUser(authUser));

      const profile = await fetchUserProfile(authUser.id);
      if (profile) {
        setUser(profile);
      }

      toast.success('Cuenta creada exitosamente');
      navigate('/dashboard');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Error al verificar el codigo'));
      throw error;
    }
  };

  const signInWithPassword = async (email: string, password: string) => {
    try {
      const { user: authUser } = await authService.signInWithPassword(email, password);

      if (!authUser) {
        throw new Error('No se pudo autenticar al usuario.');
      }

      setSupabaseUser(toAuthUser(authUser));

      const profile = await fetchUserProfile(authUser.id);
      if (!profile) {
        toast.error('Perfil de usuario no encontrado. Contacta al administrador.');
        throw new Error('Perfil no encontrado');
      }

      setUser(profile);

      toast.success('Bienvenido de vuelta');
      if (profile.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (error: unknown) {
      logger.error('[useAuth] Error en signInWithPassword:', error);
      toast.error(getErrorMessage(error, 'Credenciales invalidas'));
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setSupabaseUser(null);
      navigate('/');
      toast.success('Sesion cerrada exitosamente');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Error al cerrar sesion'));
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, supabaseUser, loading, signInWithOtp, verifyOtpAndSetPassword, signInWithPassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
